import { useSearchParams } from 'react-router-dom';

/**
 * The JSX-free half of the Settings shared module.
 *
 * Split out for the same reason `button-variants.ts` is: a file that exports both components and
 * non-components breaks Vite's fast refresh for the whole module, so editing a component in it
 * triggers a full reload rather than a hot swap.
 *
 * ── THE SELECTED BRAND LIVES IN THE URL ───────────────────────────────────────────
 *
 * `?brand=<id>`, not component state. Three reasons: switching between the three Settings screens
 * keeps the brand you were configuring, the back button behaves, and "the domain page for this brand"
 * becomes a link you can paste to somebody.
 */

/**
 * Which brand the screen is configuring, and how to change it.
 *
 * `replace: true` on the setter, because choosing a brand from a tab list is not a navigation step
 * somebody wants to walk back through one brand at a time.
 */
export function useSelectedBrand(brandIds: string[]): [string | undefined, (id: string) => void] {
  const [params, setParams] = useSearchParams();

  const requested = params.get('brand');

  // Falls back to the first brand rather than trusting the query: a stale or hand-edited id would
  // otherwise render an error where a sensible default belongs.
  const selected = requested !== null && brandIds.includes(requested) ? requested : brandIds[0];

  return [
    selected,
    (id: string) => {
      const next = new URLSearchParams(params);
      next.set('brand', id);
      setParams(next, { replace: true });
    },
  ];
}

export const inputClass =
  'h-10 w-full min-w-0 rounded-md border border-border bg-surface px-3 text-sm text-foreground placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-ring';

export const textareaClass =
  'w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-foreground placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-ring';

export function isDomain(value: string): boolean {
  const trimmed = value.trim();

  return (
    trimmed.length > 3 &&
    trimmed.includes('.') &&
    !trimmed.includes('@') &&
    !trimmed.includes('/') &&
    !trimmed.includes(' ') &&
    !trimmed.startsWith('.') &&
    !trimmed.endsWith('.')
  );
}

/**
 * Whether a text input holds a whole number inside a range.
 *
 * `Number('')` is 0 and `Number(' ')` is 0, so a blank field would validate as a valid small number
 * without the explicit emptiness check — which is how a cleared required field passes validation.
 */
export function isWholeNumberInRange(value: string, min: number, max: number): boolean {
  if (value.trim() === '') return false;

  const parsed = Number(value);

  return Number.isInteger(parsed) && parsed >= min && parsed <= max;
}
